import type { ReactNode } from 'react';
import { useOptionalDrilldown } from './DrilldownProvider';
import type { DrillRef } from '../lib/drilldown';

interface Props {
  /**
   * The record this cell points at, or null when the row can't identify one.
   * Named `target` rather than `ref` — `ref` is reserved by React.
   */
  target: DrillRef | null;
  className?: string;
  children: ReactNode;
}

/**
 * The only drill-down affordance tables use. Carries the brand-link styling the
 * app already uses for clickable identifiers, so replacing a <Link> with a
 * DrillCell is visually a no-op.
 *
 * Falls back to plain text — not a disabled button — when there is nothing to
 * open or the user may not open it, so nothing that looks clickable is a dead
 * end. The Go handler is the real enforcement; this is the UX layer.
 */
export default function DrillCell({ target, className, children }: Props) {
  // Optional on purpose: a table mounted outside the app shell (component
  // tests, a future embed) renders readable plain text instead of throwing.
  const drill = useOptionalDrilldown();

  if (!drill || !target || !drill.can(target.kind)) {
    return <span className={className}>{children}</span>;
  }
  const { open } = drill;

  return (
    <button
      type="button"
      onClick={() => open(target)}
      className={'text-tremor-brand hover:underline text-left ' + (className ?? '')}
    >
      {children}
    </button>
  );
}
